/**
 * Encryption utilities for FRISCO column-level data encryption.
 *
 * Phase 1 provides a local keyword-based PII suggestion stub.
 * When the DE team's Andes PII Detection API is ready, replace
 * `suggestPIIColumns` with the real API call.
 */

/**
 * Common keywords that indicate a column is likely to contain PII or
 * sensitive financial data.  Used by the placeholder implementation below.
 *
 * Single-token keywords (e.g. 'ssn') are matched against individual tokens
 * of the normalised column name.  Multi-token keywords (e.g. 'social_security')
 * are matched using a sliding window over consecutive tokens.
 */
const PII_KEYWORDS = [
    'ssn', 'social_security', 'tax_id', 'tin',
    'bank_account', 'account_number', 'routing_number',
    'credit_card', 'card_number', 'cvv',
    'email', 'phone', 'address', 'zip_code', 'postal',
    'salary', 'compensation', 'first_name', 'last_name',
    'dob', 'date_of_birth', 'passport', 'driver_license',
    'national_id', 'pii', 'confidential'
];

/**
 * Check whether a normalised column name matches a PII keyword using
 * token-based (word-boundary) matching instead of raw substring containment.
 *
 * - Single-token keywords: the keyword must equal one of the column's tokens.
 *   e.g. 'ssn' matches 'employee_ssn' but NOT 'ssnumber' (single token
 *   'ssnumber' ≠ 'ssn').
 * - Multi-token keywords (contain '_'): matched via a sliding window of
 *   consecutive tokens.  e.g. 'social_security' matches
 *   'employee_social_security_number'.
 *
 * @param {string[]} columnTokens – tokens of the normalised column name (split on '_')
 * @param {string} keyword – PII keyword to check
 * @returns {boolean}
 */
const matchesKeyword = (columnTokens, keyword) => {
    const keywordTokens = keyword.split('_');

    if (keywordTokens.length === 1) {
        // Single-token keyword: exact token equality
        return columnTokens.includes(keyword);
    }

    // Multi-token keyword: sliding window over column tokens
    const windowSize = keywordTokens.length;
    for (let i = 0; i <= columnTokens.length - windowSize; i++) {
        const window = columnTokens.slice(i, i + windowSize);
        if (window.every((tok, idx) => tok === keywordTokens[idx])) {
            return true;
        }
    }
    return false;
};

/**
 * Placeholder for the Andes PII Detection API.
 *
 * When the DE team's API is ready, replace the mock logic with the actual
 * API call.
 *
 * Expected real API contract:
 *   Request : POST /pii-detect  { columns: string[] }
 *   Response: { suggestions: [{ column_name: string, pii_likelihood: number }] }
 *
 * @param {string[]} columnNames – list of column names from the schema
 * @returns {Promise<string[]>} – column names suggested for encryption
 */
export const suggestPIIColumns = async (columnNames) => {
    // TODO: Replace with actual Andes PII Detection API call once available.
    //       The function signature should stay the same so callers are unaffected.

    if (!columnNames || columnNames.length === 0) {
        return [];
    }

    const suggestions = columnNames.filter((col) => {
        const normalised = col
            .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // split camelCase before lowercasing
            .toLowerCase()
            .replace(/[-\s]/g, '_');
        const tokens = normalised.split('_').filter(Boolean);
        return PII_KEYWORDS.some((keyword) => matchesKeyword(tokens, keyword));
    });

    return suggestions;
};
